import { NAVIGATION_REGISTRY } from '@/app/lib/search/navigationRegistry';

/**
 * Single source of truth for the human-readable name of a route.
 *
 * The top bar breadcrumb and the navigation loading overlay previously derived
 * this independently, which let unregistered routes render the brand name
 * ("Scarlet Sync") instead of the page they were on. Both now resolve here so a
 * route can never present two different names, and no route falls back to the
 * brand name.
 */

/** Words that stay lowercase inside a humanized slug unless they lead the title. */
const MINOR_TITLE_WORDS = new Set(['a', 'an', 'and', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'vs', 'with']);

/**
 * Routes that are not in the navigation registry, or whose slug does not
 * humanize to the name the page presents. Longest-prefix wins, matching the
 * registry lookup, so nested routes inherit a sensible parent name.
 */
const ROUTE_TITLE_ALIASES: Record<string, string> = {
  '/parking': 'Parking',
  '/professors': 'Professor Wiki',
  '/course-list': 'Course List',
  '/cold-email': 'Cold Email',
  '/tos': 'Terms of Service',
  '/privacy-policy': 'Privacy Policy',
  '/apply': 'Apply',
  '/contact': 'Contact',
  '/resources': 'Resources',
  '/developers': 'Developers',
  '/users': 'Users',
  '/admin': 'Admin',
  '/room-availability/map': 'Room Availability Map',
  '/research-outreach/drafts': 'Outreach Drafts',
  '/course-sniper/alert': 'Sniper Alert',
  '/scheduling/view': 'Schedule View',
  '/degree-search/demo': 'Degree Search Demo',
  '/guides/sources': 'Guide Sources',
  '/classes-by-time': 'Classes by Time',
  '/classes-on-map': 'Classes on Map',
  '/artifact-viewer-demo': 'Artifact Viewer',
  // Dynamic children would otherwise humanize their ID ("2721") as the page title.
  '/programs': 'Programs',
  '/about/data': 'Data Sources',
};

/**
 * Parent segments for routes that read as a child of another surface. Keeps the
 * breadcrumb hierarchical without hardcoding a per-route ladder.
 */
const BREADCRUMB_PARENTS: Record<string, string> = {
  '/courses': 'Explore',
  '/professors': 'Explore',
  '/sirs': 'Explore',
  // Degree planning is entered from the home surface and is deep enough that
  // losing the parent leaves no way back. The previous resolver rendered
  // "Dashboard > Degree Planning"; the parent is named Home here because the
  // root route was deliberately renamed, and a breadcrumb pointing at
  // "Dashboard" would name a surface that no longer exists.
  '/degree-planning': 'Home',
};

function matchesRoute(pathname: string, route: string) {
  if (route === '/') return pathname === '/';
  return pathname === route || pathname.startsWith(`${route}/`);
}

/** Turns "classes-by-time" into "Classes by Time". */
function humanizeSegment(segment: string) {
  return segment
    .replace(/-/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word, index) =>
      index > 0 && MINOR_TITLE_WORDS.has(word.toLowerCase())
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
}

/**
 * The display name for a route. Resolution order: explicit alias, navigation
 * registry entry, then a humanized final path segment. Never returns the brand
 * name — an unknown route still names itself.
 */
export function resolveRouteTitle(pathname: string): string {
  const alias = Object.keys(ROUTE_TITLE_ALIASES)
    .filter((route) => matchesRoute(pathname, route))
    .sort((left, right) => right.length - left.length)[0];
  if (alias) return ROUTE_TITLE_ALIASES[alias];

  const registryEntry = NAVIGATION_REGISTRY
    // Registry hrefs may carry query strings (e.g. /profile?tab=schedules); the
    // path is what identifies the route.
    .map((entry) => ({ entry, route: entry.href.split('?')[0] }))
    .filter(({ route }) => matchesRoute(pathname, route))
    .sort((left, right) => right.route.length - left.route.length)[0];
  if (registryEntry) return registryEntry.entry.title;

  const lastSegment = pathname.split('/').filter(Boolean).at(-1);
  return lastSegment ? humanizeSegment(lastSegment) : 'Home';
}

/**
 * The breadcrumb shown in the top bar. Adds a parent segment for routes that
 * live under another surface.
 */
export function resolveRouteBreadcrumb(pathname: string): string {
  const title = resolveRouteTitle(pathname);

  const parentRoute = Object.keys(BREADCRUMB_PARENTS)
    .filter((route) => matchesRoute(pathname, route))
    .sort((left, right) => right.length - left.length)[0];

  return parentRoute ? `${BREADCRUMB_PARENTS[parentRoute]} > ${title}` : title;
}
